<style lang="scss" scoped>
.directory {
	&__container {
		@include container;
	}
	&__panel {
		@include main-panel;
	}
	&__warning-text {
		color: #d7553d;
		font-weight: 600;
	}
	&__done {
		text-align: center;
		padding-top: 100px;
		padding-bottom: 100px;
		&__message {
			margin-top: 20px;
			font-weight: 600;
			font-size: 23px;
			color: #393942;
		}
		&__emoji {
			img {
				width: 150px;
				margin: auto;
			}
		}
	}
	&__header {
		@include flex-center;
		&__buttons {
			margin-left: auto;
			font-size: 14px;
			> div {
				margin-left: 15px;
			}
		}
	}
	&__select {
		width: 100%;
	}
	&__table {
		td,
		th {
			&:first-child {
				padding-left: 40px !important;
			}
			&:last-child {
				padding-right: 40px;
			}
		}
		td.-item {
			cursor: pointer;
		}
		&__missing-item {
			display: inline-block;
			padding-left: 10px;
			color: red;
			font-size: 16px;
			vertical-align: middle;
		}
	}
	&__footer {
		@include main-panel-footer;
		&__right {
			margin-left: auto;
		}
	}
	& ::v-deep .el-select {
		width: 100%;
	}

	&__manual-assign {
		height: 58px;
		margin: 0 0 0;
		padding-bottom: 5px;
		&__row {
			div:first-of-type {
				margin-top: 2px;
			}
			div:last-of-type {
				margin-top: -15px;
				font-weight: lighter;
			}
		}
	}

	&__warning {
		color: red;
		font-size: 20px;
		padding-top: 7px;
	}
}
</style>
<template lang="pug">
.directory
	app-header
		.directory__header(slot="header")
			h1.directory__header__title User directory
			
			.directory__header__buttons(v-if="providerCode === 'gapps' || providerCode === 'msft'")
				auto-enable-button(v-if="features.sync", :isSyncEnabled="isSyncEnabled")
				el-tooltip.item(effect='dark', :disabled="isSyncEnabled" content='Rematch users to enable use of sync', placement='bottom')
					//- ugly workaround to still show tooltip even when button is disabled
					span(style="display:inline-block; margin-left:15px")
						//- disabled property does not affect 'warning' button unlike other types 
						el-dropdown(@command='multiSelectCommand')
							el-button(:style="isSyncEnabled ? 'opacity:1' : 'opacity:0.5'" v-if="features.sync" type="warning", icon='fe fe-repeat fe-just-off', e2e="directory-run-sync", :disabled="!isSyncEnabled") Calculate MIS sync summary
								i.el-icon-arrow-down.el-icon--right
							el-dropdown-menu(slot='dropdown' v-if="isSyncEnabled")
								el-dropdown-item(v-if="features.sync" command='runCalcWithCache') Calculate with cached data
								el-dropdown-item(v-if="features.sync" command='viewCalcData') Calculate with latest data
								template(v-if="runningWork && runningWork.length")
									el-dropdown-item(v-if="features.sync" command='restartCalc') Start over
									el-dropdown-item(v-if="features.sync" command='viewCalcData') View existing calculation
				el-button(type="primary", @click="refetchUsers" style="display:inline-block; margin-left:15px").ml-auto Refetch users	
				el-tooltip.item(effect='dark', :disabled='!!selected.length', content='Please select at least one user first', placement='bottom')
					el-dropdown(@command='multiSelectCommand')
						el-button(type='primary', :disabled='!selected.length', :loading='commandRunning')
							template(v-if='selected.length || inputMatchUsers.length')
								| {{ commandRunning ? "Actioning" : "Action "}} {{selected.length}} selected
							template(v-else='')
								| Action selected
							i.el-icon-arrow-down.el-icon--right
						el-dropdown-menu(slot="dropdown")
							el-dropdown-item(v-if="features.sync", command="create") Create {{ providerName }} user{{ selected.length == 1 ? '' : 's' }}
							el-dropdown-item(v-if="features.sync", command="skip") Ignore
							el-dropdown-item(v-if="features.sync", command="activate", divided="") Unsuspend {{ providerName }} user{{ selected.length == 1 ? '' : 's' }}
							el-dropdown-item(command="unmatch") Unmatch {{ providerName }} user
							el-dropdown-item(v-if="features.sync", command="passwords", divided="") Download password{{ selected.length > 1 ? 's' : '' }}
							el-dropdown-item(v-if="features.sync", command="suspend", divided="") Suspend {{ providerName }} user{{ selected.length == 1 ? '' : 's' }}
							el-dropdown-item(v-if="features.sync", command="remove")
								.directory__warning-text Delete {{ providerName }} user{{ selected.length == 1 ? '' : 's' }}
							el-dropdown-item(v-if="features.sync && inputMatchUsers.length && !multipleMatches" command='match', divided='') Save match{{ inputMatchUsers.length > 1 ? "es" : "" }}
							el-dropdown-item(v-if="features.sync && selectedMatchedEmployees.length && providerCode !== 'apl'" command='addToClassroom', divided='') Add {{ providerName }} user{{ selectedMatchedEmployees.length == 1 ? '' : 's' }} to all classes
							el-dropdown-item(v-if="features.sync && selectedMatchedEmployees.length && providerCode !== 'apl'" command='removeFromClassroom') Use MIS data for {{ providerName }} user{{ selectedMatchedEmployees.length == 1 ? '\'s' : 's\'' }} class membership
							el-dropdown-item(v-if="features.sync && selectedMatchedEmployees.length && providerCode === 'msft'" command='setDefaultGroupOwners', divided='') Set {{ providerName }} user{{ selectedMatchedEmployees.length == 1 ? '' : 's' }} as default group owner{{ selectedMatchedEmployees.length == 1 ? '' : 's' }}
							template(v-if="providerFields[providerCode].includes('consent')")
								el-dropdown-item(v-if="features.sync", command="grantConsent", divided="") Grant consent for minor
								el-dropdown-item(v-if="features.sync", command="denyConsent") Deny consent for minor
								el-dropdown-item(v-if="features.sync", command="resetConsent") Reset consent for minor
			.directory__header__buttons(v-else)
				auto-enable-button(v-if="features.sync && synced && !isIdenticalASMTenant()", :isSyncEnabled="isSyncEnabled")
				el-button(type='primary', icon='fe fe-repeat fe-just-off', @click="goToSync", e2e="directory-run-sync", style="margin-left:15px;").ml-auto Sync Users and Classes
				el-dropdown(@command='multiSelectCommand')
					el-button(type='primary', :disabled='!selected.length', :loading='commandRunning')
						template(v-if='selected.length || inputMatchUsers.length')
							| {{ commandRunning ? "Actioning" : "Action "}} {{selected.length}} selected
						template(v-else='')
							| Action selected
						i.el-icon-arrow-down.el-icon--right
					el-dropdown-menu(slot="dropdown")
						el-dropdown-item(command="skip") Ignore user{{ selected.length == 1 ? '' : 's' }}
						el-dropdown-item(command="unmatch") Unignore user{{ selected.length == 1 ? '' : 's' }}
						el-dropdown-item(command="setCustomPersonID") Set custom person ID
	.directory__container
		.directory__panel
			el-alert( v-if="recentMigration" type="warning" center description="An MIS migration has recently been completed at your school. If you previously had Automatic Sync turned on, this has now been disabled. Please re-match any users, classes and groups that were previously matched before running any synchronisations.")
			el-alert( v-if="isAutoArchiveDisabled" type="warning" center description="Automatic archiving settings have been turned off for your school. You may reset them on the Settings page for the following academic year.")
			el-alert( v-if="organisation && schoolSettings && schoolSettings.org_sync_enabled && providerCode === 'apl'" type="error" center :description=`"Note: Your school's Apple Sync is administered by your parent organisation '" + organisation.name + "'. You can still use this page to apply filtering to Users and Classes"`)
			el-alert( v-if="providerCode === 'msft' && groupSettings.group_create_mode === 'auto' && !groupSettings.default_group_owners.length" type="warning" center description="Please select group default owner(s) to enable Microsoft Teams creation for your school’s groups.")
			stats-bar(v-if='directory && directory.WondeUsers', :results='directory.WondeUsers')
			result-filter.ml-auto.mr-2(v-model='filters', :filter-fields='fields', :remember="false" :isOrg="false")
			loading(v-if='directoryLoading || refetchingUsers')
			loading(v-else-if='commandRunning', :message="['Loading...','Doing clever things...', 'Moving things around...', 'Just one moment...']")
			template(v-else-if="showUnmatchedSuccessMessage")
				.directory__done
					.directory__done__emoji
						img(src="/emoji/celebration.png")
					.directory__done__message Great news, all users matched!
			empty(
				v-else-if="!paginated.length && directory",
				:message="!directory.WondeUsers.length ? 'No users available, please check settings' : 'No users matching search criteria'"
			)
			template(v-else)
				table.table.directory__table
					thead
						tr
							th(v-if="providerFields[providerCode].includes('options')")
								input(v-model='checkAll', type='checkbox')
							th First name
							th Last name
							th(v-if="providerCode !== 'apl'") Date of birth
							th(v-if="providerCode === 'apl'") Email
							th(v-if="providerCode === 'apl'") Person ID
							th Type
							th(width='140' v-if="providerFields[providerCode].includes('state')") State
							th(width='30' v-if="providerFields[providerCode].includes('assigned_to')")
							th(width='300' v-if="providerFields[providerCode].includes('assigned_to')") Assigned to
							th(v-if="providerCode !== 'apl'") Grace period
							th(width='50' v-if="providerFields[providerCode].includes('consent')") Consent
							th(v-if="providerFields[providerCode].includes('options') && providerFields[providerCode].includes('assigned_to')") Options
					tbody
						tr(v-for='(row, index) in paginated', @click='userCheckChange(row)', :key='index')
							td(v-if="providerFields[providerCode].includes('options')")
								input(v-model='row.selected', type='checkbox')
							td {{ row.first_name }}
							td {{ row.last_name }}
							td(v-if="providerCode !== 'apl'") {{ row.date_of_birth == 'N/A' ? row.date_of_birth : row.date_of_birth | date_convert }}
							td(v-if="providerCode === 'apl'") {{ row.email }}
							td(v-if="providerCode === 'apl'") {{ row.CustomFields.person_id.value }}
							td
								div(v-if="providerCode !== 'apl'")
									el-badge(:hidden="!providerFields[providerCode].includes('options') || allClassMembers && !allClassMembers.includes(Number(row.id))" is-dot)
										el-tooltip(:disabled="!providerFields[providerCode].includes('options') || allClassMembers && !allClassMembers.includes(Number(row.id))" placement="top" content="This employee has been added to all classes")
											span {{ row.base_type | capitalize }}
								div(v-else-if="providerCode === 'msft'")
									el-badge(:hidden="!providerFields[providerCode].includes('options') || groupSettings.default_group_owners && !groupSettings.default_group_owners.includes(row.matched_id)" is-dot)
										el-tooltip(:disabled="!providerFields[providerCode].includes('options') || groupSettings.default_group_owners && !groupSettings.default_group_owners.includes(row.matched_id)" placement="top" content="This employee has been added as a default group owner")
											span {{ row.base_type | capitalize }}
								div(v-else)
									el-badge(:hidden="!schoolSettings.org_sync_enabled || !row.primary_location" is-dot)
										el-tooltip(:disabled="!schoolSettings.org_sync_enabled || !row.primary_location" placement="top" content="This employee is in multiple locations and may require manual remidiation in ASM")
											span {{ row.base_type | capitalize }}
								span(v-else) {{ row.base_type | capitalize }}
							td(v-if="providerFields[providerCode].includes('state')")
								el-badge(:hidden="!isMarked(row)" is-dot)
									el-tooltip.item(:disabled="!isMarked(row)" placement="bottom")
										div(slot="content") 
											| This user has left your school's MIS and has therefore been marked with the status '{{ row.status }}'.
											br 
											| This will be actioned according to the time period you have allocated in your Edusync Google settings.
										el-tag(type='success', size='medium', v-if="row.status && ['matched', 'created'].includes(row.status)") matched
										el-tag(type='info', size='medium', v-else-if="row.status && row.status == 'skipped'") skipped
										el-tag(type='danger', size='medium', v-else-if="row.status && row.status == 'suspended'") suspended
										el-tag(size='warning', v-else-if="isMarked(row)") {{ row.status }}
										el-tag(size='medium', v-else='') unmatched {{row.status}}
							td(v-if="providerFields[providerCode].includes('assigned_to')")
								.directory__warning(v-if="rowDuplicated(row)")
									i.fe.fe-alert-triangle
							td(v-loading='row.assigning' v-if="providerFields[providerCode].includes('assigned_to')")
								template(v-if="row.status && ['matched', 'created', 'deleted', 'suspended'].includes(row.status)") {{ row.matched_name }}
								template(v-else-if='row.assign == true')
									el-select.w-100(:ref='`assignSelect-${row.id}`', filterable='', v-model='row.match', @change='$set(row, "selected", true)', size='medium', value-key='id', :loading='matchingLoading' :filter-method="usersFilter")
										el-option.directory__manual-assign(v-for='row in getTPUserOptions(row)', :key='row.id', :label="`${row.first_name} ${row.last_name} ${row.email}`", :value='row')
											.directory__manual-assign__row
												div {{ row.first_name }} {{ row.last_name }}
												div {{ row.email }}
								template(v-else='') Nobody
							td(v-if="providerCode !== 'apl'") {{ row.grace_at | date_convert }}
							td(v-if="providerFields[providerCode].includes('consent')") {{ row.consent_provided_for_minor }}
							td(width='100' v-if="providerFields[providerCode].includes('options') && providerFields[providerCode].includes('assigned_to')")
								el-button(type='text', :disabled="saveLoading || (row.status && ['created', 'matched'].includes(row.status)) || (row.missing) ", @click.stop='assign(row)', size='medium', :loading='row.assign && matchingLoading') Assign
				.directory__footer
					.directory__footer__left
						el-pagination( background='', layout='sizes, prev, pager, next', @size-change="handleSizeChange" :page-sizes="pageSize" :current-page.sync='page', :page-size='per_page', :total='filtered.length')
					.directory__footer__right                        
						| Showing
						strong &nbsp;{{ paginated.length }}
						| &nbsp;out of a possible
						strong &nbsp;{{ filtered.length }}
						| &nbsp;results
	user-custom-fields(:dialogVisible="userCustomFieldsDialogIsVisible", :selected="selected", :persistedFormat="userCustomFieldFormat")
</template>
<script>
import AutoEnableButton from "./../includes/AutoEnableButton";
import StatsBar from "./../includes/StatsBar";
import gql from "graphql-tag";
import _ from "lodash";
import csv from "@/libs/csv";
import Fuse from "fuse.js";
import Bugsnag from "@bugsnag/js";
import moment from "moment";
import pako from "pako";
import UserCustomFields from "../includes/UserCustomFields.vue";

export default {
	components: {
		AutoEnableButton,
		StatsBar,
		UserCustomFields
	},

	watch: {
		checkAll() {
			if (this.checkAll) {
				_.each(this.filtered, r => {
					r.selected = true;
				});
			} else {
				_.each(this.filtered, r => {
					r.selected = false;
					r.assign = false;
					r.match = null;
				});
			}
		}
	},

	data() {
		return {
			commandRunning: false,
			checkAll: false,
			filters: null,
			page: 1,
			per_page: 50,
			directory: {},
			matching: {},
			groups: [],
			runningWork: [],
			directoryLoading: true,
			matchingLoading: true,
			saveLoading: false,
			refetchingUsers: false,
			thirdPartyUsersFiltered: [],
			optionLimit: 20,
			allClassMembers: [],
			providerFields: {
				gapps: [ "first_name", "last_name", "dob", "type", "state", "assigned_to", "options" ],
				msft: [ "first_name", "last_name", "dob", "type", "state", "assigned_to", "options", "consent"],
				apl: [ "first_name", "last_name", "dob", "type", "options" ],
			},
			organisation: null,
			defaultGroupOwners: [],
			groupSettings: {
				default_group_owners: [],
				group_create_mode: "self"
			},
			userCustomFieldsDialogIsVisible: false,
			userCustomFieldFormat: null,
		};
	},

	computed: {
		fields() {
			let filterFields = [
				{
					name: "First name",
					path: "first_name",
					type: "text"
				},
				{
					name: "Last name",
					path: "last_name",
					type: "text"
				},

				{
					name: "Email",
					path: "email",
					type: "text"
				},
				{
					name: "State",
					path: "state",
					type: "radio",
					options: [
						{
							label: "Matched",
							value: "matched"
						},
						{
							label: "Unmatched",
							value: "unmatched"
						},
						{
							label: "Skipped",
							value: "skipped"
						},
						{
							label: "Suspended",
							value: "suspended"
						},
						{
							label: "Marked for suspension",
							value: "marked_for_suspension"
						},
						{
							label: "Marked for deletion",
							value: "marked_for_deletion"
						},
						{
							label: "Leaver",
							value: "leaver"
						}
					],
					value: "unmatched"
				},
				{
					name: "User type",
					path: "base_type",
					type: "radio",
					options: [
						{
							label: "Employees",
							value: "employee"
						},

						{
							label: "Students",
							value: "student"
						}
					]
				},
				
			];

			if (this.providerCode !== "apl") {
				filterFields = [
					...filterFields,
					{
						name: "Grace period",
						path: "grace_period",
						type: "radio",
						options: [
							{
								label: "N/A",
								value: "N/A"
							},

							{
								label: "Non-empty",
								value: "non-empty"
							}
						]
					}
				]
			}

			return filterFields;
		},
		showUnmatchedSuccessMessage() {
			if (this.filters && this.filters.state && this.filters.state === "unmatched") {
				// If there is only one filter active
				if (_.size(this.filters) === 1) {
					return !this.paginated.length;
				} else {
					return false;
				}
			} else {
				return false;
			}
		},
		selected() {
			return _.filter(this.filtered, { selected: true });
		},
		selectedMatchedEmployees() {
			return _.filter(this.filtered, user => user.selected && user.base_type === "employee" && ["matched", "created"].includes(user.status));
		},
		filtered() {
			if (this.directory && this.directory.WondeUsers) {
				let users = this.directory.WondeUsers;
				
				if (!_.isEmpty(this.filters)) {
					if (this.filters.first_name) {
						users =_.filter(users, user => user.first_name) // remove blank first names
						users = _.filter(users, user => (user.first_name && this.filters.first_name.filter_type == 'starts_with' ? _.startsWith(user.first_name.toLowerCase(), this.filters.first_name.value.toLowerCase()) : user.first_name.toLowerCase().includes(this.filters.first_name.value.toLowerCase())));
					}
					if (this.filters.last_name) {
						users =_.filter(users, user => user.last_name) // remove blank last names
						users = _.filter(users, user => (user.last_name && this.filters.last_name.filter_type == 'starts_with' ? _.startsWith(user.last_name.toLowerCase(), this.filters.last_name.value.toLowerCase()) : user.last_name.toLowerCase().includes(this.filters.last_name.value.toLowerCase())));
					}
					if (this.filters.state) {
						if (this.filters.state.value === "matched") {
							users = _.filter(users, user => ["created", "matched"].includes(user.status));
						}
						else if (this.filters.state.value === "unmatched") {
							users = _.filter(users, user => (user.status === null || user.status === "unmatched"));
						}
						else if(this.filters.state.value === "leaver") {
							users = _.filter(users, user => (["marked_for_suspension", "marked_for_deletion", "leaver"].includes(user.status)));
						}
						else {
							users = _.filter(users, user => (user.status ? user.status == this.filters.state.value : false));
						}
					}
					if (this.filters.email) {
						users =_.filter(users, user => this.providerCode === "apl" ? user.email : user.matched_name) // need to remove blank emails
						users = _.filter(users, user => {
							const emailValue = this.providerCode === "apl" ? user.email : user.matched_name;
							return emailValue && this.filters.email.filter_type == 'starts_with' ? _.startsWith(emailValue.toLowerCase(), this.filters.email.value.toLowerCase()) : emailValue.toLowerCase().includes(this.filters.email.value.toLowerCase())
						});
					}
					if (this.filters.base_type) {
						users = _.filter(users, { base_type: this.filters.base_type.value });
					}
					if (this.filters.group) {
						const foundGroup = _.find(this.groups, group => group.name == this.filters.group.value);
						const groupId = !_.isEmpty(foundGroup) ? foundGroup.id : null;
						users = _.filter(users, user => {
							return user.groups && user.groups.includes(groupId);
						});
					}
					if (this.filters.grace_period) {
						users = _.filter(users, user => {
							return this.filters.grace_period.value === "N/A" ? _.isEmpty(user.grace_at) : !_.isEmpty(user.grace_at);
						});
					}
					if (this.filters.class) {
						const foundClass = _.find(this.classes, cls => cls.name == this.filters.class.value);
						const classId = !_.isEmpty(foundClass) ? foundClass.id : null;
						users = _.filter(users, user => {
							return foundClass.members.includes(user.id.toString())
							
						});
					}
				}
				return _.sortBy(users, ["last_name", "first_name"]);
			} else {
				return [];
			}
		},
		paginated() {
			return _.slice(this.filtered, (this.page - 1) * this.per_page, (this.page - 1) * this.per_page + this.per_page);
		},
		multipleMatches() {
			return _(this.inputMatchUsers)
				.filter({ action: "match" })
				.countBy("spId")
				.some(qty => qty > 1);
		},
		inputMatchUsers() {
			return _(this.filtered)
				.filter(user => {
					return user.match;
				})
				.map(user => {
					return {
						idpId: user.id,
						spId: user.match.id,
						action: "match"
					};
				})
				.value();
		},

		pageSize() {
			let arrayCounter = [];
			let counter = 50;

			while (counter < this.filtered.length) {
				arrayCounter.push(counter);
				counter *= 2;
			}
			arrayCounter.push(this.filtered.length);
			return arrayCounter;
		},

		usersLoading() {
			if(this.refetchingUsers) return true;
			return this.matching && _.isEmpty(this.matching.ThirdPartyUsers);
		},
		recentMigration() {
			if (!this.isSchoolMigrated || !this.isSchoolMigrated.completed_at) {
				return false;
			}

			const completedAt = moment(this.isSchoolMigrated.completed_at);
			const oneMonthAgo = moment().subtract(1, "month");

			return completedAt.isAfter(oneMonthAgo);
		},
		isSyncEnabled() {
			const migratedStatus = !!(this.isSchoolMigrated && this.isSchoolMigrated.no_migration || this.isSchoolMigrated && this.isSchoolMigrated.completed_at !== null);

			return migratedStatus || (migratedStatus && _.filter(this.directory.WondeUsers, r => r.status && ["matched", "created"].includes(r.status)).length > 0);
		},
	},

	apollo: {
		directory: {
			query: gql`
				{
					Directory {
						id
						WondeUsers {
							id
							mis_id
							base_type
							first_name
							last_name
							date_of_birth
							email
							status
							missing
							matched_id
							matched_name
							assign
							assigning
							selected
							groups
							primary_location
							grace_at
							CustomFields {
								person_id {
									format
									value
								}
							}
							age_group
							consent_provided_for_minor
						}
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r => r.Directory,
			result() {
				this.directoryLoading = false;
			}
		},
		matching: {
			query: gql`
				{
					Directory {
						id
						ThirdPartyUsersSignedUrl
						ThirdPartyUsers {
							id
							first_name
							last_name
							display_name
							search_name
							email
						}
					}
				}
			`,
			update(r) {
				if (r.Directory && (!_.isEmpty(r.Directory.ThirdPartyUsers) || !_.isEmpty(r.Directory.ThirdPartyUsersSignedUrl))) {
					this.refetchingUsers = false;
					this.matchingLoading = false;
				}

				if(r.Directory && r.Directory.ThirdPartyUsersSignedUrl) {
					const xhr = new XMLHttpRequest();
					xhr.open("GET", r.Directory.ThirdPartyUsersSignedUrl, true);
					xhr.responseType = "arraybuffer";
					xhr.onload = function () {
						const result = pako.inflate(xhr.response, {to: 'string'});
						r.Directory.ThirdPartyUsers = JSON.parse(result);
					}
					xhr.send();
				}

				return r.Directory;
			},
			pollInterval: 3000,
			fetchPolicy: "no-cache",
			skip() {
				return !this.usersLoading;
			},
		},
		groups: {
			query: gql`
				{
					Groups {
						id
						name
						type
					}
				}
			`,
			update: r => _.cloneDeep(r.Groups),
			result() {
				_.remove(this.fields, field => field.path === "group");

				const options = this.groups ? this.groups.map(group => {
					return {
						label: `${group.name} (${group.type})`,
						value: group.name
					};
				}) : [];

				this.fields.push({
					name: "Group",
					path: "group",
					type: "radio",
					options
				});
			}
		},
		classes: {
			query: gql`
				{
					Classes {
						id
						name
						type
						members
					}
				}
			`,
			update: r => _.cloneDeep(r.Classes),
			fetchPolicy: "no-cache",
			result() {
				_.remove(this.fields, field => field.path === "class");

				const options = this.classes ? this.classes.map(cls => {
					return {
						label: `${cls.name} (${cls.type})`,
						value: cls.name
					};
				}) : [];

				this.fields.push({
					name: "Class",
					path: "class",
					type: "radio",
					options
				});
			}
		},
		isSchoolMigrated: {
			query: gql`
				{
					IsSchoolMigrated {
						schoolId
						no_migration
    					completed_at
					}
				}
			`,
			update: r => r.IsSchoolMigrated
		},
		runningWork: {
			query: gql`
				query GetWorks($where: WorkWhereInput, $order: WorkOrderByInput) {
					Work(where: $where, order: $order) {
						uuid
					}
				}
			`,
			variables: {
				where: {
					expired_at: null,
					finished_at: null,
					type: "school"
				},
				order: {
					created_at: "DESC"
				}
			},
			fetchPolicy: "network-only",
			update: r => _.cloneDeep(r.Work)
		},
		allClassMembers: {
			query: gql`
				{
					Settings {
						id
						members_of_all_classes
					}
				}
			`,
			update: r => _.cloneDeep(r.Settings.members_of_all_classes)
		},
		groupSettings: {
			query: gql`
				{
					Settings {
						id
						default_group_owners
						group_create_mode
					}
				}
			`,
			update: r => _.cloneDeep(r.Settings)
		},
		synced: {
			query: gql`
				query Activities($schema: String!) {
					Activities(schema: $schema) {
						name
					}
				}
			`,
			variables() {
				return {
					schema: "sync_finish"
				}
			},
			update(r) {
				return r.Activities.length > 0
			},
			fetchPolicy: "no-cache",
		},
		isAutoArchiveDisabled: {
			query: gql`
				{
					Settings {
						class_auto_archive
						id
					}
				}
			`,
			update: r => r.Settings.class_auto_archive === "self"
		},
		organisation: {
			query: gql`
				{
					School {
						Organisation {
							name
						}
					}
				}
			`,
			update: r => _.cloneDeep(r.School.Organisation)
		},
		schoolSettings: {
			query: gql`
				{
					Settings {
						org_sync_enabled
						sftp_host
						sftp_username
						id
					}
				}
			`,
			 update(r) {
                return _.cloneDeep(r.Settings);
            },
		},
		orgSettings: {
            query: gql`
                query Settings($type: SettingsTypeEnum!) {
                    Settings(type: $type) {
                        id
                        sftp_host
                        sftp_username
                    }
                }
            `,
            variables() {
                return {
                    type: "organisation"
                }
            },
            fetchPolicy: "cache-and-network",
            update(r) {
                return _.cloneDeep(r.Settings);
            },
        },
	},

	methods: {
		handleSizeChange(val) {
			this.directoryLoading = true;
			setTimeout(() => {
				this.per_page = val ? val : this.filtered.length;
				this.page = 1;
				if (this.paginated.length === this.per_page) {
					this.directoryLoading = false;
				}
			}, 100);
		},
		goToSync(useCache = false) {
			if (this.providerCode == "apl") {
				this.$router.push({ name: "schooldata.upload" });
			} else {
				this.$router.push({ name: "directory.sync", query: { use_cache: useCache ? 1 : 0 } });
			}
				
		},

		async multiSelectCommand(action) {
			if (action === "passwords") {
				await this.$confirm(
					"Passwords automatically assigned to users created via this app will be downloaded, but please note that any passwords changed by users will be incorrect.",
					{
						confirmButtonText: "OK",
						cancelButtonText: "Cancel",
						type: "warning"
					}
				);

				this.commandRunning = true;

				const passwords = await this.$apollo.query({
					query: gql`
						query DownloadPasswords($ids: [String!]!) {
							Passwords(ids: $ids) {
								mis_id
								first_name
								last_name
								username
								password
								created_at
							}
						}
					`,
					variables: {
						ids: _.map(_.filter(this.filtered, { selected: true }), "id")
					},
					fetchPolicy: "network-only"
				});

				if (!passwords.data.Passwords.length) {
					this.$message.error("No passwords to download");
				} else {
					let data = [];
					data.push(["MIS/SIS ID", "First name", "Last name", "User name", "Password", "Date Created"]);
					_.each(passwords.data.Passwords, r => {
						data.push([r.mis_id, r.first_name, r.last_name, r.username, r.password, moment(r.created_at).format("YYYY-MM-DD HH:mm:ss")]);
					});
					this.$message.success("File downloading");
					csv.exportToCsv("Credentials.csv", data);
				}
				this.commandRunning = false;
			} else if (action === "remove") {
				try {
					await this.$confirm("Are you sure you want to delete these users? This action is irreversible!", {
						confirmButtonText: "Yes, I'm sure",
						cancelButtonText: "Cancel",
						type: "warning"
					});

					this.commandRunning = true;

					await this.save(
						_.map(_.filter(this.filtered, { selected: true }), user => {
							return {
								idpId: user.id,
								action
							};
						})
					);
					this.commandRunning = false;
				} catch (error) {
					this.commandRunning = false;
					this.$message.error(`Failed to perform ${action} on users.`);
				}
			} else if (action === "addToClassroom") {
				try {
					const employeeIds = _.union(this.allClassMembers, _.map(_.filter(this.filtered, { selected: true, base_type: "employee" }), user => Number(user.id)));
					this.commandRunning = true;

					this.$apollo
						.mutate({
							mutation: gql`
								mutation SaveSettings($object: SettingsInput!) {
									SaveSettings(object: $object) {
										members_of_all_classes
									}
								}
							`,
							variables: {
								object: {
									members_of_all_classes: employeeIds
								}
							}
						})
						.then(r => {
							this.commandRunning = false;
							this.allClassMembers = r.data.SaveSettings.members_of_all_classes;
							this.$message.success("Settings saved. Changes will take effect on the next sync.");
						})
						.catch(e => {
							this.commandRunning = false;
							this.$message.error("Failed to update settings");
						});
				} catch (error) {
					this.commandRunning = false;
					this.$message.error(`Failed to perform ${action} on users.`);
				}
			} else if (action === "removeFromClassroom") {
				try {
					const employeeIds = _.difference(
						this.allClassMembers,
						_.map(_.filter(this.filtered, { selected: true, base_type: "employee" }), user => Number(user.id))
					);
					this.commandRunning = true;

					this.$apollo
						.mutate({
							mutation: gql`
								mutation SaveSettings($object: SettingsInput!) {
									SaveSettings(object: $object) {
										members_of_all_classes
									}
								}
							`,
							variables: {
								object: {
									members_of_all_classes: employeeIds
								}
							}
						})
						.then(r => {
							this.commandRunning = false;
							this.allClassMembers = r.data.SaveSettings.members_of_all_classes;
							this.$message.success("Settings saved. Changes will take effect on the next sync.");
						})
						.catch(e => {
							this.commandRunning = false;
							this.$message.error("Failed to update settings");
						});
				} catch (error) {
					Bugsnag.notify(error);
					this.$message.error(`Failed to perform ${action} on users.`);
					this.commandRunning = false;
				}
			} else if (action === "match") {
				try {
					if (this.multipleMatches) {
						return false;
					}

					this.commandRunning = true;
					await this.save(this.inputMatchUsers);
					this.commandRunning = false;
				} catch (error) {
					Bugsnag.notify(error);
					this.$message.error(`Failed to perform ${action} on users.`);
				}
			} else if (action === "restartCalc") {
				await this.restartCalc()
			} else if (action === "viewCalcData") {
				this.goToSync();
			} else if (action === "runCalcWithCache") {
				await this.restartCalc(true);
			} else if (action === "setDefaultGroupOwners") {
				try {
					const employeeIds = _.map(_.filter(this.filtered, { selected: true, base_type: "employee" }), user => user.matched_id);
					this.commandRunning = true;

					this.$apollo
						.mutate({
							mutation: gql`
								mutation SaveSettings($object: SettingsInput!) {
									SaveSettings(object: $object) {
										default_group_owners
									}
								}
							`,
							variables: {
								object: {
									default_group_owners: employeeIds
								}
							}
						})
						.then(r => {
							this.commandRunning = false;
							this.groupSettings.default_group_owners = r.data.SaveSettings.default_group_owners;
							this.$message.success("Settings saved.");
						})
						.catch(e => {
							this.commandRunning = false;
							this.$message.error("Failed to update settings");
						});
				} catch (error) {
					this.commandRunning = false;
					this.$message.error(`Failed to perform ${action} on users.`);
				}
			} else if(action === "setCustomPersonID") {
				const personIdFormat = _.uniqBy(this.selected, (user) => user.CustomFields.person_id.format);
				this.userCustomFieldFormat = personIdFormat.length === 1 ? _.first(personIdFormat).CustomFields.person_id.format : "";
				this.userCustomFieldsDialogIsVisible = true;
			} else if(action === "grantConsent") {
				await this.updateConsentForMinor("'Granted'");
			} else if(action === "denyConsent") {
				await this.updateConsentForMinor("'Denied'");
			} else if(action === "resetConsent") {
				await this.updateConsentForMinor("");
			} else {
				try {
					await this.save(
						_.map(_.filter(this.filtered, { selected: true }), user => {
							return {
								idpId: user.id,
								action
							};
						})
					);
				} catch (e) {
					Bugsnag.notify(e);
					this.$message.error(`Failed to perform ${action} on users.`);
					this.commandRunning = false;
				}
			}

			if (action !== "setCustomPersonID") {
				this.checkAll = false;
			}
		},

		async restartCalc(useCache = false) {
			if (this.runningWork[0] == undefined) {
				this.goToSync(useCache);
				return;
			}

			await this.$apollo
				.mutate({
					mutation: gql`
						mutation SaveWork($uuid: String!, $object: WorkInput!) {
							SaveWork(uuid: $uuid, object: $object) {
								uuid
							}
						}
					`,
					variables: {
						uuid: this.runningWork[0].uuid,
						object: {
							expired_at: moment().format("YYYY-MM-DD HH:mm:ss")
						}
					}
				})
				.then(() => {
					this.goToSync(useCache);
				});
		},

		assign(row) {
			// Set this one to show
			this.$set(row, "assign", true);
			// Focus it
			this.$nextTick(() => {
				this.$refs[`assignSelect-${row.id}`][0].focus();
			});
		},

		initSave() {
			this.matching = {};
			this.matchingLoading = true;
			this.saveLoading = true;
			this.commandRunning = true;
		},

		finishSave() {
			this.saveLoading = false;
			this.commandRunning = false;
		},

		async save(rows) {
			const perPage = 10;
			let curPage = 1;
			let promises = [];
			let chunk = _.chunk(rows, perPage)[curPage - 1];

			this.initSave();
			
			do {
				promises.push(this.updateDirectory(chunk));
				curPage++;
				chunk = _.chunk(rows, perPage)[curPage - 1];
			} while (chunk);
			
			await Promise.all(promises);
			await this.$apollo.queries.matching.refetch();
			await this.$apollo.queries.directory.refetch();
			this.finishSave();
		},

		async updateDirectory(input) {
			await this.$apollo.mutate({
				mutation: gql`
					mutation UpdateDirectory($input: [DirectoryUpdateInput!]!) {
						UpdateDirectory(input: $input) {
							id
							WondeUsers {
								id
								mis_id
								base_type
								first_name
								last_name
								email
								status
								matched_id
								matched_name
							}
						}
					}
				`,
				variables: {
					input
				}
			});
		},

		getTPUserOptions(row) {
			// The filtered list of third party users starts with a subset of the full list for better performance
			if (_.isEmpty(this.thirdPartyUsersFiltered) && !_.isEmpty(this.matching.ThirdPartyUsers)) {
				this.thirdPartyUsersFiltered = this.matching.ThirdPartyUsers.slice(0, this.optionLimit);
			}

			return this.thirdPartyUsersFiltered;
		},

		usersFilter(query) {
			// if the thirdparty users are empty or undefined just return to stop any errors
			if(_.isEmpty(this.matching.ThirdPartyUsers) || !this.matching.ThirdPartyUsers) {
				return;
			}
			const options = {
				keys: ["first_name", "last_name", "email"]
			};
			var fuse = new Fuse(this.matching.ThirdPartyUsers, options);
			this.thirdPartyUsersFiltered = fuse.search(query).slice(0, this.optionLimit);
		},

		rowDuplicated(item) {
			return (
				item.match &&
				_(this.inputMatchUsers)
					.filter({ spId: item.match.id })
					.value().length > 1
			);
		},
		duplicateUsername(item) {
			if (item.matched_name) {
				return this.filtered.filter(i => i.matched_name === item.matched_name).length > 1 || false
			}
			return false	
		},
		userCheckChange(row) {
			row.selected = !row.selected;
			if (!row.selected) {
				row.match = null;
				row.assign = false;
			}
		},

		async refetchUsers() {
			this.$confirm(
					`Re-fetching users will take some time. Are you sure you want to proceed?`,
					"Warning",
					{
						confirmButtonText: "OK",
						cancelButtonText: "Cancel",
						type: "warning"
					}
			).then(async () => {
				try {
					await this.$apollo.mutate({
						mutation: gql`
							mutation CacheClear($item: CacheTypeEnum!){
								CacheClear(item: $item)
							}`,
							variables: {
								item: "users"
							}
					});
					this.refetchingUsers = true;
					this.matching = {};
					await this.$apollo.queries.matching.refetch();
				} catch (e) {
					this.$message.error("Failed to re-fetch users");
				}
			})
		},
		saveAll() {
			const users = _.map( _.filter(this.filtered,'altered'), user => {
					return {
						idpId: user.id,
						spId: user.matched_name,
						action: "",
						type: user.base_type
					};
				})
			
			const ids = this.filtered.map(obj => obj.mis_id);
			const username = this.filtered.map(obj => obj.matched_name);

			if (this.hasDuplicates(ids) || this.hasDuplicates(username)) {
				setTimeout(() => {this.$message.error("Error! Duplicate username or ID found!");},200);
			} else {
				this.save(users);
				setTimeout(() => {this.$message.success("Usernames saved successfully")},200);
			}
		},
		hasDuplicates (arr)  {
			return  new Set(_.clone(arr.filter(n => n))).size !== arr.filter(n => n).length
		},
		csvUpload(file) {
			const alert = this.$message
			const t = this
			let reader = new FileReader();
			reader.readAsText(file);
			reader.onload = function() {
				const csv = JSON.parse(csvToJSON(reader.result));
				const ids = csv.map(obj => obj.mis_id);
				const username = csv.map(obj => obj.username);

				if (t.hasDuplicates(ids) || t.hasDuplicates(username)) {
					alert.error("Error! Duplicate username or ID found!")
				} else {
					t.directory.WondeUsers = _.map(t.directory.WondeUsers, user => {
						const check = csv.find(csvuser => csvuser.mis_id == user.mis_id)
						if (check) {
							user.matched_name = check.username
							user.altered = true
						}
						return user
					})
					t.saveAll();
					alert.success("File uploaded");
				}
				
			};
			reader.onerror = function() {
				alert.error("File upload error");
			};

			var csvToJSON = function(csv) {
				var arr = csv.split("\n")
				var jsonObj = [];
				var headers = arr[0].split(',');
				for(var i = 1; i < arr.length; i++) {
					var data = arr[i].split(',');
					var obj = {};
				for(var j = 0; j < data.length; j++) {
					obj[headers[j].trim()] = data[j].trim();
				}
					jsonObj.push(obj);
				}
				return JSON.stringify(jsonObj) // JavaScript object
			}
		},
		downloadTemplate () {
			var csvFileData = [
				['Put MIS ID here', 'Put username here'],
			];
			var csv = 'mis_id,username\n';
			csvFileData.forEach(function(row) {
				csv += row.join(',');
				csv += "\n";
			});

			var downloadCsv = document.createElement('a');
			downloadCsv.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
			downloadCsv.download = 'Sample Template.csv';
			downloadCsv.click();
		},
		isIdenticalASMTenant() {
			return this.schoolSettings &&
				this.orgSettings &&
				!this.schoolSettings.org_sync_enabled &&
				this.schoolSettings.sftp_host === this.orgSettings.sftp_host &&
				this.schoolSettings.sftp_username === this.orgSettings.sftp_username;
		},
		isMarked(row) {
			return row.status && ['suspended', 'marked_for_suspension', 'marked_for_deletion', 'leaver'].includes(row.status);
		},
		async updateConsentForMinor(formattedConsent) {
			this.commandRunning = true;
			const input = _.map(this.selected, (user) => {
                return {
                    key: "consent_provided_for_minor",
                    value: formattedConsent,
                    type: "string",
                    personId: user.id.toString(),
                };
            });

            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation UpdateUserCustomField($input: [UpdateUserCustomFieldInput!]!) {
                            UpdateUserCustomField(input: $input)
                        }
                    `,
                    variables: {
                        input 
                    },
                })
                .catch(e => {
                    Bugsnag.notify(e);
                    this.$message.error("Failed to update consent for minor. Please contact support");
                });
			
			await this.$apollo.queries.directory.refetch();
			this.commandRunning = false;
		}
	},

	mounted() {
		this.$root.$on("directory-reload-required", () => {
			window.location.reload(true);
		});
	},
	events: {
		async "visibility-set"(visibility) {
			this.userCustomFieldsDialogIsVisible = visibility;
			this.checkAll = false;
			this.refetchingUsers = true;
			await this.$apollo.queries.directory.refetch();
		}
	}
};
</script>
